// Content CMS — lesson editor. docs/next_session.md "content depth" / CLAUDE.md
// §14/§15. Lists a lesson's activities with a typed builder form per type,
// add/reorder/delete, lesson metadata, and the draft→in_review→published
// workflow. The authoring view shows FULL payloads (answer keys included).

import { Alert, Badge, Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { ActivityForm, defaultDataFor, hasTypedForm } from './-content-forms';

import { ConfirmDialog, RowActions } from '~/components';
import { ApiError } from '~/lib/api';
import {
  ACTIVITY_TYPES,
  type ActivityRow,
  type ActivityType,
  type ContentStatus,
  type ContentVersion,
  type LessonDetail,
  type LessonPreview,
  createActivity,
  deleteActivity,
  deleteLesson,
  getLesson,
  getLessonPreview,
  listLessonVersions,
  reorderActivities,
  restoreLessonVersion,
  setPublishState,
  updateActivity,
  updateLesson,
} from '~/lib/content';

export const Route = createFileRoute('/_authed/content/lessons/$id')({
  component: LessonEditorPage,
});

function LessonEditorPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const lessonQuery = useQuery({
    queryKey: ['content', 'lesson', id],
    queryFn: ({ signal }) => getLesson(id, signal),
    staleTime: 2_000,
  });

  if (lessonQuery.isLoading) return <div className="p-10 text-ink-500">Loading…</div>;
  if (lessonQuery.error || !lessonQuery.data) {
    return (
      <div className="p-10">
        <Alert tone="danger">Could not load lesson.</Alert>
        <Button variant="ghost" className="mt-4" onClick={() => navigate({ to: '/content' })}>
          ← Back to content
        </Button>
      </div>
    );
  }

  return (
    <LessonEditorInner
      lesson={lessonQuery.data.lesson}
      activities={lessonQuery.data.activities}
      onMutated={() => void qc.invalidateQueries({ queryKey: ['content', 'lesson', id] })}
      onTreeMutated={() => void qc.invalidateQueries({ queryKey: ['content', 'tree'] })}
    />
  );
}

function LessonEditorInner({
  lesson,
  activities,
  onMutated,
  onTreeMutated,
}: {
  lesson: LessonDetail;
  activities: ActivityRow[];
  onMutated: () => void;
  onTreeMutated: () => void;
}) {
  const [confirmingLesson, setConfirmingLesson] = useState(false);
  const navigate = useNavigate();
  const [titleKey, setTitleKey] = useState(lesson.titleKey);
  const [summaryKey, setSummaryKey] = useState(lesson.summaryKey ?? '');
  const [estMinutes, setEstMinutes] = useState(String(lesson.estMinutes));
  const [msg, setMsg] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const dirty = useMemo(
    () =>
      titleKey !== lesson.titleKey ||
      (summaryKey || null) !== lesson.summaryKey ||
      Number(estMinutes) !== lesson.estMinutes,
    [titleKey, summaryKey, estMinutes, lesson],
  );

  function flash(m: string): void {
    setMsg(m);
    window.setTimeout(() => setMsg(null), 2500);
  }

  const saveLesson = useMutation({
    mutationFn: () =>
      updateLesson(lesson.id, {
        titleKey,
        summaryKey: summaryKey || null,
        estMinutes: Number(estMinutes) || 10,
      }),
    onSuccess: () => {
      flash('Lesson saved.');
      onMutated();
      onTreeMutated();
    },
  });

  const delLesson = useMutation({
    mutationFn: () => deleteLesson(lesson.id),
    onSuccess: () => {
      onTreeMutated();
      void navigate({ to: '/content' });
    },
  });

  const addActivity = useMutation({
    mutationFn: (type: ActivityType) =>
      createActivity({ lessonId: lesson.id, type, data: defaultDataFor(type) }),
    onSuccess: () => onMutated(),
    onError: (e) => alert(errMsg(e)),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderActivities(lesson.id, ids),
    onSuccess: onMutated,
  });

  const publish = useMutation({
    mutationFn: (status: ContentStatus) => setPublishState('lesson', lesson.id, status),
    onSuccess: (res) => {
      flash(res.version ? `Published — version ${res.version}.` : `Status: ${res.status}.`);
      onMutated();
      onTreeMutated();
    },
  });

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="text-sm text-ink-500 hover:underline"
          onClick={() => navigate({ to: '/content' })}
        >
          ← All content
        </button>
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={() => setPreviewing(true)}>
            Preview as learner
          </Button>
          <StatusBadge status={lesson.status} />
        </div>
      </div>

      {previewing && <PreviewModal lessonId={lesson.id} onClose={() => setPreviewing(false)} />}

      <h1 className="mt-4 font-mono text-2xl font-bold text-ink-900">{lesson.titleKey}</h1>

      {msg && (
        <Alert tone="success" className="mt-4">
          {msg}
        </Alert>
      )}

      {/* Lesson metadata */}
      <section className="mt-8 rounded-2xl border border-burgundy-500/10 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-ink-900">Lesson details</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <LabeledInput label="Title key" value={titleKey} onChange={setTitleKey} mono />
          <LabeledInput label="Summary key" value={summaryKey} onChange={setSummaryKey} mono />
          <LabeledInput label="Est. minutes" value={estMinutes} onChange={setEstMinutes} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmingLesson(true)}>
            Delete lesson
          </Button>
          <Button
            onClick={() => saveLesson.mutate()}
            disabled={!dirty}
            loading={saveLesson.isPending}
          >
            Save details
          </Button>
        </div>

        <ConfirmDialog
          open={confirmingLesson}
          onOpenChange={setConfirmingLesson}
          title="Delete this lesson"
          description="The lesson and its activities are removed. Learner progress rows that reference it are orphaned, not deleted."
          confirmLabel="Delete lesson"
          tone="danger"
          onConfirm={async () => {
            await delLesson.mutateAsync();
          }}
        />
      </section>

      {/* Activities */}
      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-ink-900">
            Activities{' '}
            <span className="text-sm font-normal text-ink-500">({activities.length})</span>
          </h2>
          <ActivityAdder onAdd={(t) => addActivity.mutate(t)} loading={addActivity.isPending} />
        </div>

        {activities.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-burgundy-500/20 bg-cream-100/30 p-8 text-center text-sm text-ink-500">
            No activities yet — add one above.
          </p>
        ) : (
          <ol className="mt-4 space-y-4">
            {activities.map((a, idx) => (
              <ActivityCard
                key={a.id}
                activity={a}
                index={idx}
                total={activities.length}
                onMutated={onMutated}
                onMove={(dir) => {
                  const ids = activities.map((x) => x.id);
                  const j = dir === 'up' ? idx - 1 : idx + 1;
                  if (j < 0 || j >= ids.length) return;
                  [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
                  reorder.mutate(ids);
                }}
              />
            ))}
          </ol>
        )}
      </section>

      {/* Version history */}
      <VersionsSection lessonId={lesson.id} onRestored={flash} onMutated={onMutated} />

      {/* Publish workflow */}
      <section className="mt-10 rounded-2xl bg-burgundy-500 p-6 text-cream-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold">Publish workflow</h3>
            <p className="text-sm text-cream-100/80">
              Publishing snapshots the lesson (restorable) and makes it visible to learners. Editing
              afterwards drops it back to draft.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => publish.mutate('in_review')}
              loading={publish.isPending}
            >
              Mark in review
            </Button>
            <Button
              variant="secondary"
              onClick={() => publish.mutate('published')}
              loading={publish.isPending}
            >
              Publish
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Activity card ──────────────────────────────────────────────────────────

function ActivityCard({
  activity,
  index,
  total,
  onMutated,
  onMove,
}: {
  activity: ActivityRow;
  index: number;
  total: number;
  onMutated: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  const [confirmingActivity, setConfirmingActivity] = useState(false);
  const initial = useMemo(
    () => (isRecord(activity.payload) ? activity.payload : {}),
    [activity.payload],
  );
  const [data, setData] = useState<Record<string, unknown>>(initial);
  const [advanced, setAdvanced] = useState(!hasTypedForm(activity.type as ActivityType));
  const [error, setError] = useState<string | null>(null);
  const type = activity.type as ActivityType;

  // Reset local edits when the server payload changes (e.g. after save).
  useEffect(() => {
    setData(initial);
  }, [initial]);

  const dirty = useMemo(() => !shallowJsonEqual(data, initial), [data, initial]);

  const save = useMutation({
    mutationFn: () => updateActivity(activity.id, { data }),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        setError(e.details ? `${e.message}\n${JSON.stringify(e.details, null, 2)}` : e.message);
      } else setError(errMsg(e));
    },
  });

  const del = useMutation({
    mutationFn: () => deleteActivity(activity.id),
    onSuccess: onMutated,
  });

  return (
    <li className="rounded-2xl border border-burgundy-500/10 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-burgundy-50 px-2 text-xs font-bold text-burgundy-500">
            {index + 1}
          </span>
          <span className="font-mono text-sm text-ink-900">{activity.type}</span>
          {!activity.scoringRules.graded && <Badge tone="neutral">presentational</Badge>}
        </div>
        <div className="flex items-center gap-1">
          <MoveButtons index={index} total={total} onMove={onMove} />
          {hasTypedForm(type) && (
            <button
              type="button"
              onClick={() => setAdvanced((v) => !v)}
              className="rounded-full px-3 py-1 text-xs text-ink-500 hover:bg-cream-100"
            >
              {advanced ? 'Form' : 'JSON'}
            </button>
          )}
          <RowActions
            label="activity"
            actions={[
              {
                id: 'delete',
                label: 'Delete activity',
                icon: 'trash',
                destructive: true,
                onSelect: () => setConfirmingActivity(true),
              },
            ]}
          />
        </div>
      </div>

      <div className="mt-4">
        {advanced && hasTypedForm(type) ? (
          <JsonEditor data={data} onChange={setData} />
        ) : (
          <ActivityForm type={type} data={data} onChange={setData} />
        )}
      </div>

      <ConfirmDialog
        open={confirmingActivity}
        onOpenChange={setConfirmingActivity}
        title="Delete this activity"
        description="The activity and its payload are removed from this lesson."
        confirmLabel="Delete activity"
        tone="danger"
        onConfirm={async () => {
          await del.mutateAsync();
        }}
      />

      {error && (
        <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-heart/10 p-3 text-xs text-heart">
          {error}
        </pre>
      )}

      <div className="mt-4 flex justify-end">
        <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>
          Save activity
        </Button>
      </div>
    </li>
  );
}

function JsonEditor({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const [json, setJson] = useState(() => JSON.stringify(data, null, 2));
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setJson(JSON.stringify(data, null, 2));
  }, [data]);
  return (
    <div>
      <textarea
        value={json}
        onChange={(e) => {
          setJson(e.target.value);
          try {
            onChange(JSON.parse(e.target.value) as Record<string, unknown>);
            setErr(null);
          } catch {
            setErr('Invalid JSON — fix to save.');
          }
        }}
        rows={Math.min(20, Math.max(8, json.split('\n').length))}
        className="w-full rounded-2xl border border-burgundy-500/20 bg-white p-3 font-mono text-xs leading-relaxed text-ink-900"
      />
      {err && <p className="mt-1 text-xs text-heart">{err}</p>}
    </div>
  );
}

function ActivityAdder({
  onAdd,
  loading,
}: {
  onAdd: (type: ActivityType) => void;
  loading: boolean;
}) {
  const [type, setType] = useState<ActivityType>(ACTIVITY_TYPES[0]);
  return (
    <div className="flex items-center gap-2">
      <select
        value={type}
        onChange={(e) => setType(e.target.value as ActivityType)}
        className="rounded-2xl border border-burgundy-500/20 bg-white px-3 py-2 text-sm text-ink-900"
      >
        {ACTIVITY_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <Button onClick={() => onAdd(type)} loading={loading}>
        Add activity
      </Button>
    </div>
  );
}

// ─── Version history ──────────────────────────────────────────────────────────

function VersionsSection({
  lessonId,
  onRestored,
  onMutated,
}: {
  lessonId: string;
  onRestored: (msg: string) => void;
  onMutated: () => void;
}) {
  const [confirmingRestore, setConfirmingRestore] = useState<number | null>(null);
  const qc = useQueryClient();
  const versionsQuery = useQuery({
    queryKey: ['content', 'lesson', lessonId, 'versions'],
    queryFn: ({ signal }) => listLessonVersions(lessonId, signal),
    staleTime: 5_000,
  });

  const restore = useMutation({
    mutationFn: (version: number) => restoreLessonVersion(lessonId, version),
    onSuccess: (res) => {
      onRestored(`Restored version ${res.version} (now draft — republish to ship).`);
      onMutated();
      void qc.invalidateQueries({ queryKey: ['content', 'lesson', lessonId, 'versions'] });
    },
    onError: (e) => alert(errMsg(e)),
  });

  const versions = versionsQuery.data ?? [];

  return (
    <section className="mt-8 rounded-2xl border border-burgundy-500/10 bg-white p-6">
      <h2 className="font-display text-lg font-bold text-ink-900">Version history</h2>
      <p className="mt-1 text-sm text-ink-500">
        Each publish saves a restorable snapshot. Restoring rewrites the lesson + activities and
        drops it to draft.
      </p>
      {versionsQuery.isLoading ? (
        <p className="mt-4 text-sm text-ink-500">Loading…</p>
      ) : versions.length === 0 ? (
        <p className="mt-4 text-sm text-ink-500">No published versions yet.</p>
      ) : (
        <ol className="mt-4 divide-y divide-burgundy-500/10">
          {versions.map((v: ContentVersion) => (
            <li key={v.version} className="flex items-center justify-between py-3">
              <div>
                <span className="font-semibold text-ink-900">Version {v.version}</span>
                <span className="ms-3 text-xs text-ink-500">
                  {new Date(v.createdAt).toLocaleString()}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmingRestore(v.version)}
                loading={restore.isPending && restore.variables === v.version}
              >
                Restore
              </Button>
            </li>
          ))}
        </ol>
      )}

      <ConfirmDialog
        open={confirmingRestore != null}
        onOpenChange={(open) => !open && setConfirmingRestore(null)}
        title={`Restore version ${confirmingRestore ?? ''}`}
        description="The current draft is overwritten by this version. That replacement cannot be undone from here — take a snapshot first if the draft matters."
        confirmLabel="Restore version"
        tone="danger"
        onConfirm={async () => {
          if (confirmingRestore == null) return;
          await restore.mutateAsync(confirmingRestore);
          setConfirmingRestore(null);
        }}
      />
    </section>
  );
}

// ─── Preview as learner ───────────────────────────────────────────────────────
// A read-only, learner-shaped view of the lesson (answer keys stripped). The
// admin has no i18n runtime, so we show the translation keys + structure — enough
// to confirm the lesson is well-formed before publishing.

function PreviewModal({ lessonId, onClose }: { lessonId: string; onClose: () => void }) {
  const previewQuery = useQuery({
    queryKey: ['content', 'lesson', lessonId, 'preview'],
    queryFn: ({ signal }) => getLessonPreview(lessonId, signal),
    staleTime: 0,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl bg-cream-50 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Lesson preview"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-ink-900">Preview as learner</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 place-items-center rounded-full text-ink-500 hover:bg-cream-200"
          >
            ✕
          </button>
        </div>
        <p className="mt-1 text-xs text-ink-500">
          Exactly what a learner receives — answer keys stripped. Text shows translation keys (no
          i18n runtime in admin).
        </p>

        {previewQuery.isLoading ? (
          <p className="mt-6 text-ink-500">Loading…</p>
        ) : previewQuery.error || !previewQuery.data ? (
          <Alert tone="danger" className="mt-6">
            Could not load preview.
          </Alert>
        ) : (
          <PreviewBody preview={previewQuery.data} />
        )}
      </div>
    </div>
  );
}

function PreviewBody({ preview }: { preview: LessonPreview }) {
  return (
    <div className="mt-5">
      <div className="rounded-xl bg-white p-4 ring-1 ring-burgundy-500/10">
        <p className="font-mono text-sm font-bold text-burgundy-600">{preview.lesson.titleKey}</p>
        <p className="mt-1 text-xs text-ink-500">
          {preview.lesson.subjectSlug} · {preview.lesson.grade} · {preview.lesson.estMinutes}m ·{' '}
          {preview.activities.length} activities
        </p>
      </div>
      <ol className="mt-4 space-y-3">
        {preview.activities.map((a, i) => (
          <li key={a.id} className="rounded-xl bg-white p-4 ring-1 ring-burgundy-500/10">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-burgundy-50 px-1.5 text-xs font-bold text-burgundy-500">
                {i + 1}
              </span>
              <span className="font-mono text-sm text-ink-900">{a.type}</span>
              {!a.scoringRules.graded && <Badge tone="neutral">presentational</Badge>}
              {a.scoringRules.graded && (
                <span className="text-xs text-ink-500">
                  pass ≥ {a.scoringRules.passScore} · {a.scoringRules.xp} XP
                </span>
              )}
            </div>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-cream-100/60 p-3 text-xs leading-relaxed text-ink-700">
              {JSON.stringify(a.payload, null, 2)}
            </pre>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─── Shared UI ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContentStatus }) {
  const tone: Parameters<typeof Badge>[0]['tone'] =
    status === 'published' ? 'success' : status === 'in_review' ? 'warning' : 'neutral';
  return <Badge tone={tone}>{status.replace('_', ' ')}</Badge>;
}

function LabeledInput({
  label,
  value,
  onChange,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) {
  return (
    <label className="block text-sm font-medium text-ink-700">
      {label}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 ${mono ? 'font-mono text-xs' : ''}`}
      />
    </label>
  );
}

function MoveButtons({
  index,
  total,
  onMove,
}: {
  index: number;
  total: number;
  onMove: (dir: 'up' | 'down') => void;
}) {
  return (
    <span className="flex flex-col">
      <button
        type="button"
        onClick={() => onMove('up')}
        disabled={index === 0}
        aria-label="Move up"
        className="text-[10px] leading-none text-ink-500 hover:text-ink-900 disabled:opacity-25"
      >
        ▲
      </button>
      <button
        type="button"
        onClick={() => onMove('down')}
        disabled={index === total - 1}
        aria-label="Move down"
        className="text-[10px] leading-none text-ink-500 hover:text-ink-900 disabled:opacity-25"
      >
        ▼
      </button>
    </span>
  );
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function shallowJsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
